/* Per-Style image gallery with per-Size selection.
   - Selecting a Style loads that Style's image group (rebuilds thumbnails).
   - Selecting a Size jumps the main image + highlights the thumbnail at that
     Size's position within the group.
   Re-binds its own handlers on every render, so it never depends on Dawn's
   media-gallery diffing. */
if (!customElements.get("variant-media-gallery")) {
  customElements.define(
    "variant-media-gallery",
    class VariantMediaGallery extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;
        this.mainImg = this.querySelector(".vmg__main-img");
        this.thumbs = this.querySelector(".vmg__thumbs");
        this.zoomBtn = this.querySelector(".vmg__zoom");

        try {
          this.model = JSON.parse(this.querySelector(".vmg__data").textContent);
        } catch (e) {
          this.model = { styles: {}, variantMap: {} };
        }
        this.styles = this.model.styles || {};
        this.variantMap = this.model.variantMap || {};

        this.currentStyle = null;
        this.currentSize = null;
        this.images = [];
        this.sizeImageIndex = 0;
        this.activeIndex = 0;

        this.renderForVariant(this.dataset.currentVariant);
        this.bindVariantChange();
        this.bindMainSwipe();

        if (this.zoomBtn) {
          this.zoomBtn.addEventListener("click", () =>
            this.openLightbox(this.activeIndex),
          );
        }
        if (this.mainImg) {
          this.mainImg.style.cursor = "zoom-in";
          this.mainImg.addEventListener("click", () => {
            if (this.suppressClick) return;
            this.openLightbox(this.activeIndex);
          });
        }
      }

      bindMainSwipe() {
        const main = this.querySelector(".vmg__main");
        if (!main) return;
        let startX = 0;
        let startY = 0;
        let tracking = false;
        this.suppressClick = false;
        main.addEventListener(
          "touchstart",
          (e) => {
            tracking = e.touches.length === 1;
            if (!tracking) return;
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
          },
          { passive: true },
        );
        main.addEventListener(
          "touchend",
          (e) => {
            if (!tracking) return;
            tracking = false;
            const t = e.changedTouches[0];
            const dx = t.clientX - startX;
            const dy = t.clientY - startY;
            if (
              this.images.length > 1 &&
              Math.abs(dx) > 40 &&
              Math.abs(dx) > Math.abs(dy) * 1.5
            ) {
              // A real swipe: switch image and swallow the click that follows.
              this.suppressClick = true;
              setTimeout(() => (this.suppressClick = false), 400);
              const len = this.images.length;
              this.setMain((this.activeIndex + (dx < 0 ? 1 : -1) + len) % len);
            }
          },
          { passive: true },
        );
      }

      bindVariantChange() {
        // Dawn dispatches a bubbling `change` on the hidden id input when the
        // variant updates (product-info.js -> updateVariantInputs).
        document.addEventListener("change", (e) => {
          const t = e.target;
          if (!t || !t.matches) return;
          if (
            !t.matches(
              `#product-form-${this.section} input[name="id"], #product-form-installment-${this.section} input[name="id"]`,
            )
          )
            return;
          this.renderForVariant(t.value);
        });
      }

      renderForVariant(id) {
        const map = this.variantMap[id];
        if (!map) return;
        const group = this.styles[map.style];
        if (!group || !group.images || !group.images.length) return;

        if (map.style !== this.currentStyle) {
          // Style changed (or first render): show the product photo (first image).
          this.currentStyle = map.style;
          this.currentSize = map.size;
          this.images = group.images;
          this.sizeImageIndex = Math.max(
            0,
            Math.min(group.sizeImageIndex || 0, group.images.length - 1),
          );
          this.buildThumbs(group.images);
          this.setMain(0);
        } else if (map.size !== this.currentSize) {
          // Size changed within the same Style: jump to the single size image.
          this.currentSize = map.size;
          this.setMain(this.sizeImageIndex);
        }
      }

      buildThumbs(images) {
        this.thumbs.innerHTML = images
          .map(
            (im, i) =>
              `<li class="vmg__thumb-item"><button type="button" class="vmg__thumb" data-index="${i}"><img src="${
                im.thumb
              }" alt="${escapeAttr(im.alt)}" loading="lazy" width="100" height="100"></button></li>`,
          )
          .join("");
        this.thumbs.querySelectorAll(".vmg__thumb").forEach((btn) => {
          const i = parseInt(btn.dataset.index, 10);
          btn.addEventListener("click", () => this.setMain(i));
        });
      }

      setMain(i) {
        const im = this.images[i];
        if (!im) return;
        this.activeIndex = i;
        if (this.mainImg) {
          this.mainImg.src = im.full;
          this.mainImg.alt = im.alt || "";
        }
        this.thumbs.querySelectorAll(".vmg__thumb").forEach((b, idx) => {
          b.classList.toggle("is-active", idx === i);
        });
      }

      /* ---------------- Fullscreen lightbox with pinch-zoom ---------------- */
      openLightbox(startIndex) {
        const images = this.images;
        if (!images.length) return;
        let index = startIndex || 0;

        const overlay = document.createElement("div");
        overlay.className = "vmg-lightbox";
        overlay.innerHTML =
          '<button type="button" class="vmg-lightbox__close" aria-label="Close">&times;</button>' +
          '<button type="button" class="vmg-lightbox__nav vmg-lightbox__prev" aria-label="Previous">&#8249;</button>' +
          '<button type="button" class="vmg-lightbox__nav vmg-lightbox__next" aria-label="Next">&#8250;</button>' +
          '<div class="vmg-lightbox__stage"><img class="vmg-lightbox__img" alt=""></div>' +
          '<div class="vmg-lightbox__counter"></div>';
        document.body.appendChild(overlay);
        document.body.style.overflow = "hidden";

        const img = overlay.querySelector(".vmg-lightbox__img");
        const counter = overlay.querySelector(".vmg-lightbox__counter");
        const stage = overlay.querySelector(".vmg-lightbox__stage");
        const state = { scale: 1, x: 0, y: 0 };
        const pointers = new Map();
        let start = null;

        const apply = () => {
          img.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
        };
        const reset = () => {
          state.scale = 1;
          state.x = 0;
          state.y = 0;
          apply();
        };
        const clampPan = () => {
          const r = stage.getBoundingClientRect();
          const maxX = Math.max(
            0,
            (img.offsetWidth * state.scale - r.width) / 2,
          );
          const maxY = Math.max(
            0,
            (img.offsetHeight * state.scale - r.height) / 2,
          );
          state.x = Math.min(maxX, Math.max(-maxX, state.x));
          state.y = Math.min(maxY, Math.max(-maxY, state.y));
        };

        const show = (i) => {
          index = (i + images.length) % images.length;
          img.src = images[index].zoom || images[index].full;
          img.alt = images[index].alt || "";
          counter.textContent = `${index + 1} / ${images.length}`;
          reset();
        };

        overlay
          .querySelector(".vmg-lightbox__close")
          .addEventListener("click", close);
        overlay
          .querySelector(".vmg-lightbox__prev")
          .addEventListener("click", (e) => {
            e.stopPropagation();
            show(index - 1);
          });
        overlay
          .querySelector(".vmg-lightbox__next")
          .addEventListener("click", (e) => {
            e.stopPropagation();
            show(index + 1);
          });
        overlay.addEventListener("click", (e) => {
          if (e.target === overlay || e.target === stage) close();
        });
        const onKey = (e) => {
          if (e.key === "Escape") close();
          else if (e.key === "ArrowLeft") show(index - 1);
          else if (e.key === "ArrowRight") show(index + 1);
        };
        document.addEventListener("keydown", onKey);

        function close() {
          document.removeEventListener("keydown", onKey);
          document.body.style.overflow = "";
          overlay.remove();
        }

        let lastTap = 0;
        img.addEventListener("pointerdown", (e) => {
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          img.setPointerCapture(e.pointerId);
          if (pointers.size === 1) {
            start = {
              mode: "pan",
              x: e.clientX,
              y: e.clientY,
              sx: state.x,
              sy: state.y,
            };
            const now = e.timeStamp;
            if (now - lastTap < 300) {
              if (state.scale > 1) reset();
              else {
                state.scale = 2.5;
                clampPan();
                apply();
              }
              lastTap = 0;
              start = null;
            } else {
              lastTap = now;
            }
          } else if (pointers.size === 2) {
            const p = [...pointers.values()];
            start = {
              mode: "pinch",
              dist: Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y),
              scale: state.scale,
            };
          }
        });
        img.addEventListener("pointermove", (e) => {
          if (!pointers.has(e.pointerId)) return;
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (pointers.size >= 2 && start && start.mode === "pinch") {
            const p = [...pointers.values()];
            const dist = Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y);
            state.scale = Math.min(
              4,
              Math.max(1, start.scale * (dist / start.dist)),
            );
            clampPan();
            apply();
            e.preventDefault();
          } else if (
            pointers.size === 1 &&
            start &&
            start.mode === "pan" &&
            state.scale > 1
          ) {
            state.x = start.sx + (e.clientX - start.x);
            state.y = start.sy + (e.clientY - start.y);
            clampPan();
            apply();
            e.preventDefault();
          }
        });
        const up = (e) => {
          pointers.delete(e.pointerId);
          try {
            img.releasePointerCapture(e.pointerId);
          } catch (err) {
            /* no-op */
          }
          if (pointers.size === 0) {
            // Not zoomed in: a horizontal drag switches to the prev/next image.
            if (
              start &&
              start.mode === "pan" &&
              state.scale <= 1.01 &&
              images.length > 1
            ) {
              const dx = e.clientX - start.x;
              const dy = e.clientY - start.y;
              if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                show(index + (dx < 0 ? 1 : -1));
              }
            }
            start = null;
            if (state.scale <= 1.01) reset();
          }
        };
        img.addEventListener("pointerup", up);
        img.addEventListener("pointercancel", up);

        show(index);
      }
    },
  );
}

function escapeAttr(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
